import type {
  StableSwapConfig,
  StableSwapState,
  SwapTokenInfo,
} from "@saberhq/stableswap-sdk";
import { u64 } from "@saberhq/token-utils";
import { useConnectionContext } from "@saberhq/use-solana";
import * as Sentry from "@sentry/react";
import type { TokenInfo } from "@solana/spl-token-registry";
import { PublicKey } from "@solana/web3.js";
import { mapValues } from "lodash";
import { useMemo } from "react";

import type { CurrencyMarket } from "../currencies";
import type { PoolID } from "../environments";
import poolsInfoDevnet from "./data/pools-info.devnet.json";
import poolsInfoMainnet from "./data/pools-info.mainnet.json";

export interface SubsidiesInfo {
  distributor: PublicKey;
  silo: PublicKey;
  subsidies: PublicKey;
  rewardsToken: TokenInfo;
}

export interface PoolInfo {
  id: PoolID;
  name: string;
  tokens: readonly [TokenInfo, TokenInfo];
  tokenIcons: readonly [TokenInfo, TokenInfo];
  underlyingIcons: readonly [TokenInfo, TokenInfo];
  currency: CurrencyMarket;
  lpToken: TokenInfo;

  plotKey: PublicKey;
  swap: {
    config: StableSwapConfig;
    state: StableSwapState;
  };
  harvest: {
    farm: PublicKey;
    /**
     * Subsidies of SBR.
     */
    subsidies: SubsidiesInfo[];
  };
  hidden?: boolean;
}

export interface PoolsInfoData {
  addresses: {
    landlord: PublicKey;
    landlordBase: PublicKey;
  };
  pools: PoolInfo[];
}

type PoolInfoRaw = Omit<PoolInfo, "swap" | "harvest"> & {
  swap: {
    config: {
      swapAccount: string;
      authority: string;
      swapProgramID: string;
      tokenProgramID: string;
    };
    state: StableSwapStateRaw;
  };
  harvest: {
    farm: PublicKey;
    subsidies: {
      distributor: string;
      silo: string;
      subsidies: string;
      rewardsToken: TokenInfo;
    }[];
  };
  hidden?: boolean;
};

type SwapTokenInfoRaw = {
  [K in keyof SwapTokenInfo]: string;
};

type StableSwapStateRaw = {
  [K in keyof StableSwapState]: StableSwapState[K] extends PublicKey | u64
    ? string
    : StableSwapState[K] extends SwapTokenInfo
    ? SwapTokenInfoRaw
    : StableSwapState[K];
};

const parseRawSwapState = (state: StableSwapStateRaw): StableSwapState => {
  return {
    poolTokenMint: new PublicKey(state.poolTokenMint),
    adminAccount: new PublicKey(state.adminAccount),

    tokenA: valuesToKeys(state.tokenA),
    tokenB: valuesToKeys(state.tokenB),

    initialAmpFactor: new u64(state.initialAmpFactor, "hex"),
    targetAmpFactor: new u64(state.targetAmpFactor, "hex"),
    startRampTimestamp: state.startRampTimestamp,
    stopRampTimestamp: state.stopRampTimestamp,

    fees: state.fees,
  };
};

// interface PoolsInfoDataRaw {
//   addresses: {
//     [K in keyof PoolsInfoData["addresses"]]: string;
//   };
//   pools: PoolInfoRaw[];
// }

/**
 * Use pools info from the API.
 * @returns
 */
export const usePoolsInfo = (): {
  loading: boolean;
  data: PoolsInfoData | null | undefined;
} => {
  const { network } = useConnectionContext();
  const data =
    network === "mainnet-beta"
      ? poolsInfoMainnet
      : network === "devnet"
      ? poolsInfoDevnet
      : null;
  if (data === null) {
    Sentry.captureException(new Error(`Invalid network ${network}`));
  }

  const dataParsed = useMemo(() => {
    if (!data) {
      return data;
    }
    return {
      addresses: valuesToKeys(data.addresses),
      pools: data.pools.map((poolRaw: unknown) => {
        const pool = poolRaw as PoolInfoRaw;
        return {
          ...pool,
          swap: {
            config: valuesToKeys(pool.swap.config),
            state: parseRawSwapState(pool.swap.state),
          },
          harvest: {
            farm: new PublicKey(pool.harvest.farm),
            subsidies: pool.harvest.subsidies.map((subsidy) => ({
              distributor: new PublicKey(subsidy.distributor),
              silo: new PublicKey(subsidy.silo),
              subsidies: new PublicKey(subsidy.subsidies),
              rewardsToken: subsidy.rewardsToken,
            })),
          },
        };
      }),
    };
  }, [data]);

  return {
    loading: data === undefined,
    data: dataParsed,
  };
};

const valuesToKeys = <T extends Record<string, string>>(
  raw: T
): { [K in keyof T]: PublicKey } =>
  mapValues(raw, (addr) => new PublicKey(addr));
